"use client";

import { useState, useEffect, useRef } from "react";
import { Box, Heading, Flex, useColorModeValue, Text, Skeleton, Card, CardHeader, CardBody, Badge, Alert, AlertIcon, AlertTitle, AlertDescription, Button, Collapse, Icon, Tooltip, VStack, Container } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { FaChartBar, FaMedal, FaChartArea, FaLayerGroup, FaLightbulb } from "react-icons/fa";

import KpiCards from "./components/KpiCards";
import Table1 from "./components/Table1";
import Top10A from "./components/Top10A";
import Top10B from "./components/Top10B";
import Top10C from "./components/Top10C";
import Top10D from "./components/Top10D";
import Chart1 from "./amcharts/Chart1";
import Chart2 from "./amcharts/Chart2";
import Chart4 from "./components/Chart4";
import Chart5 from "./components/Chart5";
import Chart6 from "./components/Chart6";
import Chart7 from "./amcharts/Chart7";
import Chart8 from "./amcharts/Chart8";
import Chart9 from "./amcharts/Chart9";
import Chart10 from "./amcharts/Chart10";
import ChkRegionTabs from "./components/ChkRegionTabs";

import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext";
import { usePageAccess } from "../../wrappers/PageAccessContext";

// Scroll-triggered reveal hook
const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px", ...options }
    );
    observer.observe(node);
    return () => observer.unobserve(node);
  }, []);

  return [ref, isVisible];
};

// Custom accordion section component
const AccordionSection = ({ title, isOpen, onToggle, children, supplier, retailer, icon, theme, index = 0 }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const [revealRef, isVisible] = useScrollReveal();

  return (
    <Box
      ref={revealRef}
      opacity={isVisible ? 1 : 0}
      transform={isVisible ? "translateY(0)" : "translateY(30px)"}
      transition={`opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`}
    >
    <Card mb={4} boxShadow={isOpen ? "md" : "sm"} bg={cardBg} borderColor={borderColor} borderWidth="1px" transition="all 0.3s ease" _hover={{ boxShadow: "md" }}>
      <CardHeader
        bg={`linear-gradient(135deg, ${theme.colors.primary1}, ${theme.colors.primary2})`}
        color="white"
        borderTopRadius="md"
        py={2.5}
        px={4}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        cursor="pointer"
        onClick={onToggle}
        transition="all 0.3s ease"
        _hover={{
          bg: `linear-gradient(135deg, ${theme.colors.primary2}, ${theme.colors.primary1})`,
          "& .section-icon": {
            transform: "rotate(15deg) scale(1.2)",
          },
          "& .section-title": {
            letterSpacing: "0.15em",
            textShadow: "0 0 12px rgba(255, 255, 255, 0.4)",
          },
        }}
      >
        <Flex align="center" gap={2}>
          {icon && (
            <Icon
              className="section-icon"
              as={icon}
              boxSize={4}
              color="whiteAlpha.900"
              transition="transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)"
            />
          )}
          <Heading
            className="section-title"
            size="sm"
            fontWeight="bold"
            color="white"
            letterSpacing="wide"
            transition="letter-spacing 0.4s ease, text-shadow 0.4s ease"
          >
            {title}
          </Heading>
        </Flex>
        <Flex align="center" gap={3}>
          <Flex gap={2} display={{ base: "none", md: "flex" }}>
            <Badge bg="whiteAlpha.200" color="white" px={2} py={0.5} borderRadius="md" fontSize="2xs" fontWeight="medium">
              {supplier}
            </Badge>
            <Badge bg="whiteAlpha.200" color="white" px={2} py={0.5} borderRadius="md" fontSize="2xs" fontWeight="medium">
              {retailer}
            </Badge>
          </Flex>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="26px"
            height="26px"
            borderRadius="full"
            bg="whiteAlpha.200"
            _hover={{ bg: "whiteAlpha.300", transform: "scale(1.1)" }}
            transition="all 0.2s ease"
          >
            {isOpen ? <ChevronUpIcon boxSize={4} /> : <ChevronDownIcon boxSize={4} />}
          </Box>
        </Flex>
      </CardHeader>

      <Collapse in={isOpen} animateOpacity>
        <CardBody bg={sectionBg} p={2} borderBottomRadius="md">
          {children}
        </CardBody>
      </Collapse>
    </Card>
    </Box>
  );
};

const Sales = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const theme = require("@chakra-ui/react").useTheme();

  useEffect(() => {
    const userid = localStorage.getItem("userid");
    if (userid === "1" || userid === "2" || userid === 1 || userid === 2) {
      setIsAdmin(true);
    }
  }, []);
  const [openSections, setOpenSections] = useState({
    salesReport: true,
    top10Stores: true,
    salesTrends: true,
    additionalAnalytics: true,
    forecastInsights: true,
  });

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const [showRestrictedMessage, setShowRestrictedMessage] = useState(false);
  const { selectedRetailer, selectedSupplier } = useSupplierRetailer();
  const [loading, setLoading] = useState(false);
  const { restrictedyn } = usePageAccess();
  const key = `${selectedSupplier}-${selectedRetailer}`;

  const cardBg = useColorModeValue("rgba(255,255,255,0.6)", "rgba(45,55,72,0.6)");
  const borderColor = useColorModeValue("rgba(255,255,255,0.4)", "rgba(255,255,255,0.1)");

  useEffect(() => {
    if (restrictedyn) {
      setShowRestrictedMessage(true);
    } else {
      setShowRestrictedMessage(false);
    }
  }, [restrictedyn]);

  if (showRestrictedMessage) {
    return (
      <Alert status="warning" variant="subtle" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" height="200px" borderRadius="md" boxShadow="md">
        <AlertIcon boxSize="40px" mr={0} />
        <AlertTitle mt={4} mb={1} fontSize="lg">
          Account Payment Required
        </AlertTitle>
        <AlertDescription maxWidth="md">
          Please pay your account fee to access the Sales Report.
          <Button mt={4} colorScheme="blue" size="sm" onClick={() => (window.location.href = "/myProfile")}>
            Go to My Profile
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Shared card styling for content cards
  const contentCardProps = {
    bg: cardBg,
    borderRadius: "md",
    boxShadow: "sm",
    borderWidth: "1px",
    borderColor: borderColor,
    backdropFilter: "blur(12px)",
    transition: "all 0.3s ease",
    _hover: {
      boxShadow: `0 8px 30px rgba(88, 182, 192, 0.2)`,
      transform: "translateY(-2px)",
    },
    position: "relative",
    overflow: "hidden",
    _before: {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "3px",
      bg: `linear-gradient(to right, ${theme.colors.primary1}, ${theme.colors.primary2})`,
      borderTopLeftRadius: "md",
      borderTopRightRadius: "md",
      transition: "height 0.3s ease",
    },
    sx: {
      "&:hover::before": {
        height: "4px",
      },
    },
  };

  return (
    <Box minH="100vh" bg="gray.50" px={2} py={4}>
      <Container maxW="99.5vw" mx="auto">
        <VStack spacing={0} align="stretch">
          {/* KPI Summary Cards - admin only */}
            <Box mb={4}>
              <KpiCards key={`kpi-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
            </Box>

          {/* Sales Report Section */}
          <AccordionSection title="Sales Report" isOpen={openSections.salesReport} onToggle={() => toggleSection("salesReport")} supplier={selectedSupplier} retailer={selectedRetailer} icon={FaChartBar} theme={theme} index={0}>
            {loading ? (
              <Box p={6}>
                <Skeleton height="400px" />
              </Box>
            ) : (
              <Box maxHeight="700px" overflowY="auto" maxWidth="97vw">
                <Table1 key={`tbl-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
              </Box>
            )}
          </AccordionSection>

          {/* Top 10 Stores Analysis Section */}
          <AccordionSection title="Top 10 Stores Analysis" isOpen={openSections.top10Stores} onToggle={() => toggleSection("top10Stores")} supplier={selectedSupplier} retailer={selectedRetailer} icon={FaMedal} theme={theme} index={1}>
            <Flex direction={{ base: "column", lg: "row" }} wrap="wrap" justify="space-between" align="flex-start" gap={4}>
              <Box flex="1" minW="300px" p={4} {...contentCardProps}>
                <Top10A key={`t1-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
              </Box>
              <Box flex="1" minW="300px" p={4} {...contentCardProps}>
                <Top10B key={`t1-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
              </Box>
              <Box flex="1" minW="300px" p={4} {...contentCardProps}>
                <Top10C key={`t1-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
              </Box>
              {selectedRetailer === "PNP" && (
                <Box flex="1" minW="300px" p={4} {...contentCardProps}>
                  <Top10D key={`t1d-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
                </Box>
              )}
            </Flex>
          </AccordionSection>

          {/* Sales Trends Section */}
          <AccordionSection title="Sales Trends" isOpen={openSections.salesTrends} onToggle={() => toggleSection("salesTrends")} supplier={selectedSupplier} retailer={selectedRetailer} icon={FaChartArea} theme={theme} index={2}>
            <Flex direction={{ base: "column", lg: "row" }} wrap="wrap" gap={4}>
              <Box flex="1" minW="300px" height="350px" p={4} {...contentCardProps} overflow="visible" css={{ "& > div": { overflow: "visible !important" }, "& canvas": { maxWidth: "none !important" } }}>
                <Chart1 key={`c1-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
              </Box>
              <Box flex="1" minW="300px" height="350px" p={4} {...contentCardProps} overflow="visible" css={{ "& > div": { overflow: "visible !important" }, "& canvas": { maxWidth: "none !important" } }}>
                <Chart2 key={`c2-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
              </Box>
            </Flex>
          </AccordionSection>

          {/* Additional Analytics Section */}
          <AccordionSection title="Additional Analytics" isOpen={openSections.additionalAnalytics} onToggle={() => toggleSection("additionalAnalytics")} supplier={selectedSupplier} retailer={selectedRetailer} icon={FaLayerGroup} theme={theme} index={3}>
            {selectedRetailer === "CHK" ? (
              <ChkRegionTabs supplier={selectedSupplier} retailer={selectedRetailer} contentCardProps={contentCardProps} keyStr={key} />
            ) : (
              <Flex direction={{ base: "column", lg: "row" }} wrap="wrap" gap={4}>
                <Box flex="1" minW="300px" height="350px" p={4} {...contentCardProps} overflow="visible" css={{ "& > div": { overflow: "visible !important" }, "& canvas": { maxWidth: "none !important" } }}>
                  <Chart4 supplier={selectedSupplier} retailer={selectedRetailer} />
                </Box>
                <Box flex="1" minW="300px" height="350px" p={4} {...contentCardProps} overflow="visible" css={{ "& > div": { overflow: "visible !important" }, "& canvas": { maxWidth: "none !important" } }}>
                  <Chart5 supplier={selectedSupplier} retailer={selectedRetailer} />
                </Box>
                <Box flex="1" minW="300px" height="350px" p={4} {...contentCardProps} overflow="visible" css={{ "& > div": { overflow: "visible !important" }, "& canvas": { maxWidth: "none !important" } }}>
                  <Chart6 supplier={selectedSupplier} retailer={selectedRetailer} />
                </Box>
              </Flex>
            )}
          </AccordionSection>

          {/* Forecast & Inventory Insights Section */}
          <AccordionSection title="Forecast & Inventory Insights" isOpen={openSections.forecastInsights} onToggle={() => toggleSection("forecastInsights")} supplier={selectedSupplier} retailer={selectedRetailer} icon={FaLightbulb} theme={theme} index={4}>
            {isAdmin && (
              <Flex direction={{ base: "column", lg: "row" }} wrap="wrap" gap={4} mb={4}>
                <Box flex="1" minW="300px" height="350px" p={4} {...contentCardProps} overflow="visible" css={{ "& > div": { overflow: "visible !important" }, "& canvas": { maxWidth: "none !important" } }}>
                  <Chart7 key={`c7-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
                </Box>
                <Box flex="1" minW="300px" height="350px" p={4} {...contentCardProps} overflow="visible" css={{ "& > div": { overflow: "visible !important" }, "& canvas": { maxWidth: "none !important" } }}>
                  <Chart8 key={`c8-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
                </Box>
              </Flex>
            )}

            <Flex direction={{ base: "column", lg: "row" }} wrap="wrap" gap={4} mb={4}>
              {isAdmin && (
                <Box flex="1" minW="300px" height="350px" p={4} {...contentCardProps} overflow="visible" css={{ "& > div": { overflow: "visible !important" }, "& canvas": { maxWidth: "none !important" } }}>
                  <Chart9 key={`c9-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
                </Box>
              )}
              <Box flex="1" minW="300px" height="350px" p={4} {...contentCardProps} overflowY="auto" overflowX="hidden">
                <Chart10 key={`c10-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
              </Box>
            </Flex>
          </AccordionSection>
        </VStack>
      </Container>
    </Box>
  );
};

export default Sales;
